// import { Link } from 'react-router-dom';
import { ArrowForwardIcon, WarningIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { useApplicationContext } from "contexts/ApplicationContext";
import { ProfileMenu } from "./profile-menu";
import { NotificationBell } from "./notification-bell";
import { getNavbarLogo } from "components/white-label";
import { useRouter } from "router-manager/parts";
import { memo } from "react";
import { parseUserType } from "utils/util";

export interface NavbarProps {
  withOutSearch?: boolean;
  HeadRender?: () => JSX.Element | null;
}

export const navbarWidth = "80px";

function Navbar({ HeadRender }: NavbarProps) {
  const { user } = useApplicationContext();
  const { userData } = user;
  const router = useRouter();

  const exceptions = ["/usuario-bloqueado"].includes(router.pathname);

  const isUserVendedor =
    userData.type === "PARCEIRO_VENDEDOR" ||
    userData.type === "PARCEIRO_OPERACIONAL";
  const isUserMaster = userData.type === "PARCEIRO_MASTER";

  const quantidadeContratadaExcedida =
    !!user?.userData.customerLicenseUserQtyExceeded;
  const licencaExpirada = !!user?.userData.customerLicenseUserExpired;

  const alertConfig = (() => {
    if (licencaExpirada && isUserVendedor) {
      return {
        lines: [
          "Licença de usuários expirada! Contate o usuário MASTER para regularização.",
          "Seu usuário está sujeito a bloqueio a qualquer momento."
        ]
      };
    }

    if (quantidadeContratadaExcedida && isUserVendedor) {
      return {
        lines: [
          "Quantidade de usuários Vendedor/Operacional ativos excedida para a sua licença!",
          "Contate o usuário MASTER para regularização!",
          "Seu usuário está sujeito a bloqueio a qualquer momento."
        ]
      };
    }

    if (licencaExpirada && isUserMaster) {
      return {
        lines: [
          "Pagamento pendente! Licença de usuários expirada!",
          "Seu usuário está sujeito a bloqueio a qualquer momento."
        ],
        ctaLabel: router.pathname !== "/confirm-licenses" ? "Pagar" : undefined,
        minW: "74px"
      };
    }

    if (quantidadeContratadaExcedida && isUserMaster) {
      return {
        lines: [
          "Quantidade de usuários MASTER ativos excedida para a sua licença!",
          "Seu usuário está sujeito a bloqueio a qualquer momento."
        ],
        ctaLabel:
          router.pathname !== "/confirm-licenses" ? "Regularizar" : undefined,
        minW: "106px"
      };
    }

    return null;
  })();

  const shouldShowExpiredPaymentAlert = !!alertConfig && !exceptions;

  return (
    <Flex
      w="100%"
      h={navbarWidth}
      bg="#fff"
      justifyContent="space-between"
      alignItems="center"
      px="2rem"
      borderBottom="1px solid #E8EAED"
      gap="2rem"
    >
      <Flex alignItems="center">
        <Box>{getNavbarLogo()}</Box>
      </Flex>

      <Flex justifyContent="center" minW="0">
        {shouldShowExpiredPaymentAlert ? (
          <Flex
            w="100%"
            maxW="780px"
            bg="#FFF7ED"
            border="1px solid #FED7AA"
            borderLeft="4px solid #EA580C"
            borderRadius="10px"
            px="12px"
            py="8px"
            align="center"
            justify="space-between"
            gap="10px"
          >
            <Flex align="center" gap="8px" minW="0">
              <Icon as={WarningIcon} color="#C2410C" boxSize="16px" />
              <Box color="#9A3412" fontSize="12px" lineHeight="1.3">
                {alertConfig?.lines.map((line) => (
                  <Text fontWeight="medium" key={line}>
                    {line}
                  </Text>
                ))}
              </Box>
            </Flex>

            {alertConfig?.ctaLabel ? (
              <Button
                size="xs"
                rightIcon={<ArrowForwardIcon />}
                bg="#EA580C"
                _hover={{ bg: "#C2410C" }}
                color="white"
                onClick={() => router.push("/confirm-licenses")}
                whiteSpace="nowrap"
                minW={alertConfig.minW}
              >
                {alertConfig.ctaLabel}
              </Button>
            ) : null}
          </Flex>
        ) : null}
      </Flex>

      <Flex alignItems="center">
        {HeadRender ? <HeadRender /> : null}
        <Box color="custom-gray">
          <Text mr="10px" mb="4px" fontWeight="400" lineHeight="1.2">
            {userData.name}{" "}
            <Text as="span" textTransform="capitalize">
              ({parseUserType.get(userData.type!)})
            </Text>
          </Text>
          <Text mr="10px" fontWeight="800" lineHeight="1.2">
            {userData.customerName}
          </Text>
        </Box>
        <NotificationBell />
        <ProfileMenu />
      </Flex>
    </Flex>
  );
}

export default memo(Navbar);
